import { Application } from 'pixi.js';

export type BackgroundRecordingApplication = Application & {
  __pixleBackgroundRecording?: boolean;
  __pixleDestroyAfterBackgroundRecording?: boolean;
  destroyed?: boolean;
};

export const asBackgroundRecordingApp = (
  app: Application
): BackgroundRecordingApplication => app as BackgroundRecordingApplication;

export const isBackgroundRecordingApp = (
  app: Application | null | undefined
): boolean =>
  Boolean(app && asBackgroundRecordingApp(app).__pixleBackgroundRecording);

/**
 * Returns true when the given PixiJS application has been destroyed.
 *
 * `Application.destroy()` tears the app down and nulls its `stage`, so a
 * previously-captured `app` reference can still be truthy while its `stage` is
 * gone. Guarding on this prevents "Cannot read properties of null (reading
 * 'addChild')"-style errors in async continuations that started while the app
 * was alive but resolved after a scene-boundary teardown.
 */
export const isPixiAppDestroyed = (
  app: Application | null | undefined
): boolean => Boolean(app && (asBackgroundRecordingApp(app).destroyed || !app.stage));

export const markBackgroundRecordingApp = (
  app: Application,
  destroyAfterRecording = false
) => {
  const recordingApp = asBackgroundRecordingApp(app);
  recordingApp.__pixleBackgroundRecording = true;
  recordingApp.__pixleDestroyAfterBackgroundRecording = destroyAfterRecording;
};

export const clearBackgroundRecordingApp = (app: Application): boolean => {
  const recordingApp = asBackgroundRecordingApp(app);
  const shouldDestroy = Boolean(
    recordingApp.__pixleDestroyAfterBackgroundRecording
  );

  recordingApp.__pixleBackgroundRecording = false;
  recordingApp.__pixleDestroyAfterBackgroundRecording = false;

  return shouldDestroy;
};
